import React, { useMemo } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import { useTranslate } from 'react-admin';
import { BREAKPOINTS } from '../../theme/breakpoints';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import ShowNoteSignsItem from '../notes/subcomponents/ShowNoteSignsItem';

// Ids of the signature components on the Poser step of the Rikutec processes (maître d'ouvrage, maître d'oeuvre).
const OWNER_SIGNATURE_ID = '_form16';
const INSTALLER_SIGNATURE_ID = '_form17';

const useStyles = makeStyles(() => ({
    list: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 16,
        maxWidth: 1000,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    card: {
        backgroundColor: CRM_COLORS.surface,
        border: `1px solid ${CRM_COLORS.border}`,
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 0,
    },
    head: { display: 'flex', alignItems: 'center', gap: 10 },
    title: { fontSize: '1rem', fontWeight: 600, color: CRM_COLORS.secondary },
    pill: {
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 8px',
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    dot: { width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' },
    image: {
        '& img': {
            display: 'block',
            width: '100%',
            height: 160,
            objectFit: 'contain',
            boxSizing: 'border-box',
            backgroundColor: CRM_COLORS.surfaceMuted,
            border: `1px solid ${CRM_COLORS.border} !important`,
            borderRadius: `${CRM_RADIUS.control}px !important`,
        },
    },
    empty: {
        height: 160,
        boxSizing: 'border-box',
        backgroundColor: CRM_COLORS.surfaceMuted,
        border: `1px dashed ${CRM_COLORS.borderStrong}`,
        borderRadius: CRM_RADIUS.control,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        color: CRM_COLORS.text.secondary,
        fontSize: '0.875rem',
    },
    signer: { fontSize: '0.8125rem', color: CRM_COLORS.text.secondary },
}));

interface Props {
    notes: any[];
    hasOwnerSignature?: boolean | null;
    hasInstallerSignature?: boolean | null;
    ownerName?: string | null;
    installerName?: string | null;
}

/** The two signatures of the process always keep their place; any other found in the history follows. */
const InstallationSignatures = ({
    notes,
    hasOwnerSignature,
    hasInstallerSignature,
    ownerName,
    installerName,
}: Props) => {
    const t = useTranslate();
    const classes = useStyles();

    // The notes come most recent first, so the first signature met for an id is the current one.
    const { owner, installer, others } = useMemo(() => {
        const signatures = notes
            .flatMap((note) => note?.data?.signatures || [])
            .filter((signature) => !!signature?.customFields?.imageDataBase64);
        return {
            owner: signatures.find((signature) => signature.id === OWNER_SIGNATURE_ID),
            installer: signatures.find((signature) => signature.id === INSTALLER_SIGNATURE_ID),
            others: signatures.filter(
                (signature) => signature.id !== OWNER_SIGNATURE_ID && signature.id !== INSTALLER_SIGNATURE_ID,
            ),
        };
    }, [notes]);

    const card = (
        key: React.Key,
        title: string,
        alt: string,
        signature: any,
        signed: boolean | null | undefined,
        signer?: string,
    ) => {
        const image = signature?.customFields?.imageDataBase64;
        const isSigned = !!signed || !!image;
        return (
            <Box key={key} className={classes.card}>
                <Box className={classes.head}>
                    <Typography className={classes.title} component='h3'>
                        {title}
                    </Typography>
                    <span className={classes.pill} style={isSigned ? CRM_COLORS.status.ok : CRM_COLORS.status.wait}>
                        <span className={classes.dot} aria-hidden />
                        {t(isSigned ? 'app.ancsV2.signatures.signed' : 'app.ancsV2.signatures.pending')}
                    </span>
                </Box>
                {image ? (
                    <Box className={classes.image}>
                        <ShowNoteSignsItem imageDataBase64={image} name={alt} hideName />
                    </Box>
                ) : (
                    <Box className={classes.empty}>
                        <CreateOutlinedIcon aria-hidden />
                        {t(isSigned ? 'app.ancsV2.signatures.unavailable' : 'app.ancsV2.signatures.missing')}
                    </Box>
                )}
                {signer && <Typography className={classes.signer}>{signer}</Typography>}
            </Box>
        );
    };

    const signerLine = (name: string | null | undefined, role: string) => (name ? `${name} · ${t(role)}` : t(role));

    return (
        <Box className={classes.list} role='tabpanel' aria-label={t('app.ancsV2.groups.signatures')}>
            {card(
                'owner',
                t('app.ancsV2.signatures.owner'),
                t('app.ancsV2.columns.hasOwnerSignature'),
                owner,
                hasOwnerSignature,
                signerLine(ownerName, 'app.ancsV2.signatures.ownerRole'),
            )}
            {card(
                'installer',
                t('app.ancsV2.signatures.installer'),
                t('app.ancsV2.columns.hasInstallerSignature'),
                installer,
                hasInstallerSignature,
                signerLine(installerName, 'app.ancsV2.signatures.installerRole'),
            )}
            {others.map((signature: any, index: number) => {
                const name = signature?.customFields?.name || t('app.ancsV2.signatures.others');
                return card(signature.id || index, name, name, signature, true);
            })}
        </Box>
    );
};

export default InstallationSignatures;
